import {
  Job,
  LocaleEnum,
  QueryRecommendationsArgs,
  QuerySearchJobsArgs,
  Readable,
  RecommendationsQueryVariables,
  SearchableEnum,
  useRecommendationsLazyQuery,
  useSearchJobsLazyQuery,
} from 'generated/apolloComponents'
import React, { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import useTranslation from 'next-translate/useTranslation'
import Script from 'next/script'
import dynamic from 'next/dynamic'
import { useAnalyticsPush } from '@/context/analyticsContext'
import Comments from '@/components/organisms/Comments/Comments'
import { formatDate, getReadingTime } from '@/helpers/utils'
import Time from '@/components/atoms/Time'
import NewsDate from '@/components/atoms/NewsDate'
import Avatar from '@/components/atoms/Avatar'
import { useMe } from '@/context/meContext'
import { useCountry } from '@/context/countryContext'
import Content from './Content'
// import Banner from '@/components/molecules/Banner' // Not used in production

interface IProps {
  readable: Readable
}

const SingleNoPhotoReadable = ({ readable }: IProps) => {
  const articleDate = formatDate(
    readable.updated && readable.updated > readable.publishedAt
      ? readable.updated
      : readable.publishedAt,
    'pl-PL'
  )

  const authorLink = () =>
    readable.author?.kind === 'organization'
      ? `/companies/profiles/${readable.author?.id}`
      : `/readme/authors/${readable.author?.id}`

  const readingTime = getReadingTime(Number(readable.wordCount))
  const [ssrWindow, setSsrWindow] = useState()
  const [offers, setOffers] = useState<Job[]>()
  const { me, loadMe } = useMe()
  const { pushEvent } = useAnalyticsPush()
  const { lang } = useTranslation('common')
  const { country } = useCountry()

  const DynamicRelatedJobs = dynamic(
    () => import('../../molecules/ReadableRelatedJobsList'),
    { ssr: false }
  )
  const DynamicRelatedReadables = dynamic(
    () => import('../../molecules/ReadableRelatedReadableList'),
    { ssr: false }
  )
  const DynamicHighlight = dynamic(
    () => import('../../molecules/ReadableHighlight'),
    { ssr: false }
  )

  const variables: QueryRecommendationsArgs = {
    perPage: 3,
    page: 1,
    language: LocaleEnum[lang as 'pl' | 'en'],
    resourceId: readable.id,
    resourceType: SearchableEnum.Readable,
    returnType: SearchableEnum.Job,
    boostWhere: {
      type: 'with_salary',
      value: 'true',
      factor: 500,
    },
  }

  const [recommendationsQuery] = useRecommendationsLazyQuery({
    onCompleted: (data) => {
      if (data && data.recommendations) {
        setOffers(data.recommendations as Job[])
      }
    },
  })
  const [searchJobsQuery] = useSearchJobsLazyQuery({
    onCompleted: (data) => {
      if (data && data.searchJobs.nodes && data.searchJobs.nodes.length > 0) {
        setOffers(data.searchJobs.nodes as Job[])
      } else {
        recommendationsQuery({
          variables: variables as RecommendationsQueryVariables,
        })
      }
    },
  })

  const fetchRecommendations = useCallback(async () => {
    if (me && me.rejectedJobs) variables.exclude = [...me.rejectedJobs]
    if (readable.company) {
      const companyVariables: QuerySearchJobsArgs = {
        page: 1,
        perPage: 3,
        language: LocaleEnum[lang as 'pl' | 'en'],
        filters: {
          company: [readable.company?.id.split('-')[0]],
        },
        boostWhere: readable.mainTag
          ? {
              type: 'technologies',
              value: readable.mainTag,
              factor: 500,
            }
          : null,
      }
      if (me && me.rejectedJobs) companyVariables.exclude = [...me.rejectedJobs]
      searchJobsQuery({ variables: companyVariables })
    } else {
      recommendationsQuery({
        variables: variables as RecommendationsQueryVariables,
      })
    }
  }, [readable, me])

  useEffect(() => {
    const windowLocation: any = window.location
    setSsrWindow(windowLocation)
  }, [])

  useEffect(() => {
    loadMe()
    fetchRecommendations()
  }, [fetchRecommendations])

  useEffect(() => {
    if (window) {
      pushEvent({
        event: 'viewed',
        articleData: {
          id: readable.title,
          type: readable.readableType,
          title: readable.title,
          technology: readable.mainTag,
          role: readable.roles,
          category:
            readable.categories &&
            readable.categories?.map((item, index) => item.key),
          seniority: readable.experiences,
          tier: readable.tier,
          forIT: true,
          language: readable.language,
        },
      })
    }
  }, [readable])

  // const shouldShowBanner = !readable.company

  return (
    <>
      {/* {shouldShowBanner && <Banner />} */}
      <main className='mt-14 mb-24'>
        <article className='container container-smallup article'>
          <h1 className='font-bold md:text-6xl w-full'>{readable.title}</h1>

          {/* Subtitle / Intro */}
          {readable.intro && (
            <div className='font-normal text-base md:text-3xl leading-7 md:leading-normal mt-4 w-full'>
              {readable.intro}
            </div>
          )}

          {/* Author section */}
          <div className='flex items-center gap-4 my-8'>
            {readable.author?.photo && (
              <a href={authorLink()}>
                <Avatar
                  url={readable.author?.photo.url}
                  size='normal'
                  alt={readable.author?.name as string}
                />
              </a>
            )}

            {readable.author?.companyLogo && (
              <a href={authorLink()}>
                <img
                  width='1'
                  height='1'
                  alt={readable.author.company as string}
                  src={readable.author.companyLogo.url ?? ''}
                  className='max-h-[48px] w-auto object-contain'
                />
              </a>
            )}

            <div className='flex flex-col'>
              <div className='flex items-center gap-2'>
                <span className='text-xl font-medium'>
                  {readable.author?.name || readable.author?.company}
                </span>
                {readable.author?.company && readable.author?.name && (
                  <span className='text-sm text-gray-600'>
                    @ {readable.author.company}
                  </span>
                )}
              </div>
              <div className='flex gap-4 text-sm text-gray-500'>
                <NewsDate date={articleDate} className='inline-block' />
                <Time time={readingTime} className='inline-block' />
              </div>
            </div>
          </div>

          {/* Tags */}
          {readable.categories && readable.categories.length > 0 && (
            <div className='my-8'>
              {readable.categories.map((item, index) => (
                <Link
                  href={`/readme/s/categories,${item.key}`}
                  key={index}
                  prefetch={false}
                  className='mr-2 mb-3 inline-block rounded-4xl border px-6 py-1.5 hover:bg-gray-100 border-gray-100'
                >
                  {item.titles && item.titles[lang as 'pl' | 'en']}
                </Link>
              ))}
            </div>
          )}
          <div className='flex flex-col md:flex-row relative'>
            <Content readable={readable} offers={offers} />
          </div>
        </article>
        <div className='container-medium mt-14'>
          <div className='md:px-40'>
            <Comments readable={readable} />
          </div>
        </div>
        <div className='container'>
          <section className='mt-14 md:mt-24'>
            <DynamicRelatedJobs
              readable={readable}
              locale={lang as LocaleEnum}
              country={country}
            />
          </section>
          <DynamicRelatedReadables
            readable={readable}
            locale={lang as LocaleEnum}
            perPage={3}
          />
        </div>

        <Script
          strategy='afterInteractive'
          dangerouslySetInnerHTML={{
            __html: `
          var loadImg = function() {
            for(const img of document.querySelectorAll("img[data-src]")) {
              const url = img.getAttribute('data-src');
              const newImg = new Image();
              newImg.onload = function() {
                img.src = url;
              };
              newImg.src = url;
            }
          };
          loadImg();
        `,
          }}
        />
        <DynamicHighlight />
      </main>
    </>
  )
}

export default SingleNoPhotoReadable
